import { Button } from '../../ui/button';
import {
  ArrowLeft, MessageSquare, Mail, Copy, Check,
} from 'lucide-react';
import { DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { introExpiryNotice } from '../../../lib/introExpiry';
import { useAuth } from '../../../lib/auth';

/**
 * Step 2 of the Make-Intros dialog: confirm + send.
 *
 * Two-person summary at the top, copy-link rows in the middle, message
 * preview in a collapsed `<details>`, and the SMS/Email delivery buttons
 * at the bottom. All side effects (clipboard, sms:, mailto:) are handled
 * by the parent via the `onCopy*` / `onSendSms` / `onSendEmail` callbacks.
 */
export default function IntrosSendStep({
  intro,
  pickedContacts,
  linkA,
  linkB,
  body,
  copiedA,
  copiedB,
  onBack,
  onCopyA,
  onCopyB,
  onSendSms,
  onSendEmail,
}) {
  const { user } = useAuth();
  const senderName = user?.first_name?.trim() || user?.username?.trim();
  return (
    <div className="flex flex-col max-h-[calc(100dvh-2rem)] sm:max-h-[85vh] min-w-0">
      <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
        <div className="flex items-center gap-2">
          <button onClick={onBack} className="p-1 rounded-full hover:bg-secondary/60" data-testid="make-intros-back-btn">
            <ArrowLeft size={16} />
          </button>
          <DialogTitle className="font-heading">Send the intro</DialogTitle>
        </div>
        <DialogDescription className="text-xs">
          Each link opens a compact preview with “Add to PeopleiKnow” + “Save to Contacts”.
        </DialogDescription>
        <p className="text-xs text-muted-foreground" data-testid="make-intros-acknowledgment">
          {senderName ? `${senderName}, you` : 'You'} acknowledge that the recipients would consent to receiving this intro.
        </p>
        <p className="text-xs text-muted-foreground" data-testid="make-intros-expiry">
          {introExpiryNotice(intro.expires_at, true, false)}
        </p>
      </DialogHeader>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4 min-w-0">
        {/* Pair summary */}
        <div className="grid grid-cols-2 gap-3" data-testid="make-intros-pair-summary">
          <IntroPersonSummary contact={pickedContacts[0]} forName={pickedContacts[1]?.first_name} link={linkA} testId="intro-side-a" />
          <IntroPersonSummary contact={pickedContacts[1]} forName={pickedContacts[0]?.first_name} link={linkB} testId="intro-side-b" />
        </div>

        {/* Copy each link */}
        <div className="space-y-2">
          <CopyRow
            label={`Link for ${pickedContacts[0]?.first_name}`}
            value={linkA}
            copied={copiedA}
            onCopy={onCopyA}
            testId="copy-link-a"
          />
          <CopyRow
            label={`Link for ${pickedContacts[1]?.first_name}`}
            value={linkB}
            copied={copiedB}
            onCopy={onCopyB}
            testId="copy-link-b"
          />
        </div>

        {/* Message preview */}
        <details className="rounded-lg border border-border p-3 bg-card overflow-hidden">
          <summary className="text-xs font-medium cursor-pointer text-muted-foreground">
            Preview the message
          </summary>
          <pre className="mt-2 text-[11px] whitespace-pre-wrap break-all text-muted-foreground font-mono" data-testid="intro-message-preview">
            {body}
          </pre>
        </details>
      </div>

      {/* Delivery buttons */}
      <div className="px-5 py-3 border-t border-border bg-card space-y-2">
        <Button
          onClick={onSendSms}
          disabled={!intro.delivery.recipients.phones.length}
          className="w-full h-11 rounded-full bg-amber-600 hover:bg-amber-700 text-white font-heading font-semibold"
          data-testid="make-intros-send-sms-btn"
        >
          <span className="flex items-center gap-2"><MessageSquare size={16} /> Text the two contacts</span>
        </Button>
        <Button
          onClick={onSendEmail}
          disabled={!intro.delivery.recipients.emails.length}
          variant="outline"
          className="w-full h-11 rounded-full border-amber-500/40 text-amber-700 hover:bg-amber-500/10 font-heading font-semibold"
          data-testid="make-intros-send-email-btn"
        >
          <span className="flex items-center gap-2"><Mail size={16} /> Send group email</span>
        </Button>
        {!intro.delivery.recipients.phones.length && !intro.delivery.recipients.emails.length && (
          <p className="text-[11px] text-amber-600 text-center">
            Neither contact has a phone or email saved — use the copy buttons above.
          </p>
        )}
      </div>
    </div>
  );
}

function IntroPersonSummary({ contact, forName, link, testId }) {
  if (!contact) return null;
  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className="block rounded-xl border border-border p-3 hover:border-amber-500/50 transition-colors"
      data-testid={testId}
    >
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Will receive</p>
      <p className="font-medium text-sm mt-0.5 truncate">
        {contact.first_name} {contact.last_name}
      </p>
      <p className="text-[11px] text-muted-foreground mt-1">
        opens <span className="text-amber-700 dark:text-amber-400">{forName}</span>&apos;s card
      </p>
    </a>
  );
}

function CopyRow({ label, value, copied, onCopy, testId }) {
  return (
    <div className="flex items-center gap-2 p-2 rounded-lg bg-secondary/40 border border-border">
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
        <p className="text-[11px] font-mono truncate" data-testid={`${testId}-value`}>{value}</p>
      </div>
      <button
        onClick={onCopy}
        className="shrink-0 px-2 py-1 rounded-full text-xs font-medium hover:bg-amber-500/10 text-amber-700 dark:text-amber-400 transition-colors"
        data-testid={testId}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
      </button>
    </div>
  );
}
